import React, { useCallback, useState } from 'react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { IconButton, useModal, AddIcon, MinusIcon, MetamaskIcon } from 'toonswap-uikit'
import { useWeb3React } from '@web3-react/core'
import UnlockButton from 'components/UnlockButton'
import { useERC20 } from 'hooks/useContract'
import { useSousApprove } from 'hooks/useApprove'
import { useTranslation } from 'contexts/Localization'
import { useSousStake } from 'hooks/useStake'
import { useSousUnstake } from 'hooks/useUnstake'
import { getBalanceNumber } from 'utils/formatBalance'
import { getAddress } from 'utils/addressHelpers'
import { useSousHarvest } from 'hooks/useHarvest'
import { registerToken } from 'utils/wallet'
import { BASE_URL } from 'config'
import Balance from 'components/Balance'
import { PoolCategory } from 'config/constants/types'
import { Pool } from 'state/types'
import DepositModal from '../../DepositModal'
import WithdrawModal from '../../WithdrawModal'

import {
  ActionContainer as ActionCard,
  ActionTitles,
  ActionContent,
  Title,
  Subtle,
  Earned as EarnedValue,
  Staked as StakedValue,
  StyledPrimaryButton,
  ValueWrapper,
} from './styles'

const Container = styled.div`
  position: relative;
  z-index: 1;
  background: #475355;
  border: 3px solid #262d2d;
  border-top: none;
  border-radius: 8px 8px 26px 26px;
  box-shadow: 0 -3px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  width: 100%;
  flex-direction: column-reverse;
  padding: 16px;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries.lg} {
    flex-direction: row;
    padding: 40px 20px 24px 20px;
    gap: 16px;
    align-items: stretch;
  }
`

const InfoContainer = styled.div`
  min-width: 0;
  background: #374244;
  border: 3px solid #252c2d;
  border-radius: 26px;
  padding: 14px 16px;
  width: auto;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 180px;
  }
`

const ActionsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 16px;
  flex: 1;
  min-width: 0;
`

const IconButtonWrapper = styled.div`
  display: flex;
  gap: 6px;
`

const PairInfoButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: #59c2e3;
  cursor: pointer;
`

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`

interface PoolActionPanelProps {
  pool: Pool
}

const PoolActionPanel: React.FC<PoolActionPanelProps> = ({ pool }) => {
  const { sousId, stakingToken, earningToken, harvest, poolCategory, totalStaked, userData, stakingLimit, isFinished } =
    pool

  const isCorePool = poolCategory === PoolCategory.CORE
  const { t } = useTranslation()
  const stakingTokenContract = useERC20(stakingToken.address ? getAddress(stakingToken.address) : '')
  const { account } = useWeb3React()
  const { onApprove } = useSousApprove(stakingTokenContract, sousId)
  const { onStake } = useSousStake(sousId, isCorePool)
  const { onUnstake } = useSousUnstake(sousId)
  const { onReward } = useSousHarvest(sousId, isCorePool)

  const [requestedApproval, setRequestedApproval] = useState(false)
  const [pendingTx, setPendingTx] = useState(false)

  const allowance = new BigNumber(userData?.allowance || 0)
  const stakingTokenBalance = new BigNumber(userData?.stakingTokenBalance || 0)
  const stakedBalance = new BigNumber(userData?.stakedBalance || 0)
  const earnings = new BigNumber(userData?.pendingReward || 0)

  const needsApproval = allowance.isEqualTo(0) || allowance.isLessThan(stakingTokenBalance)
  const displayEarned = getBalanceNumber(earnings, earningToken.decimals)
  const displayStaked = getBalanceNumber(stakedBalance, stakingToken.decimals)

  const convertedLimit = new BigNumber(stakingLimit || 0).multipliedBy(new BigNumber(10).pow(earningToken.decimals))
  const [onPresentDeposit] = useModal(
    <DepositModal
      max={stakingLimit && stakingTokenBalance.isGreaterThan(convertedLimit) ? convertedLimit : stakingTokenBalance}
      onConfirm={onStake}
      tokenName={stakingLimit ? `${stakingToken.symbol} (${stakingLimit} max)` : stakingToken.symbol}
      stakingTokenDecimals={stakingToken.decimals}
    />,
  )
  const [onPresentWithdraw] = useModal(
    <WithdrawModal
      max={stakedBalance}
      onConfirm={onUnstake}
      tokenName={stakingToken.symbol}
      stakingTokenDecimals={stakingToken.decimals}
    />,
  )

  const handleApprove = useCallback(async () => {
    try {
      setRequestedApproval(true)
      const txHash = await onApprove()
      if (!txHash) {
        setRequestedApproval(false)
      }
    } catch (e) {
      console.error(e)
    }
  }, [onApprove])

  return (
    <Container>
      <InfoContainer>
        {totalStaked && (
          <ValueWrapper>
            <span>{t('Total Staked')}</span>
            <Balance
              fontSize="14px"
              isDisabled={isFinished}
              color="#ffffff"
              value={getBalanceNumber(totalStaked, stakingToken.decimals)}
            />
          </ValueWrapper>
        )}
        {earningToken.address && (
          <LinkRow>
            {/* <StyledLink
              onClick={() =>
                registerToken(
                  getAddress(earningToken.address),
                  earningToken.symbol,
                  earningToken.decimals,
                  `${BASE_URL}/images/tokens/${earningToken.symbol.toLowerCase()}.png`,
                )
              }
            >
              {t('Add to Metamask')}
              <MetamaskIcon height={15} width={15} ml="4px" />
            </StyledLink> */}
          </LinkRow>
        )}
        {earningToken.projectLink && (
          <LinkRow>
            <a
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                width: '100%',
                justifyContent: 'space-between',
              }}
              href={earningToken.projectLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PairInfoButton>{t('View Project Site')}</PairInfoButton>
              <img src="/images/link_to.svg" alt="info" width={14} height={14} />
            </a>
          </LinkRow>
        )}
      </InfoContainer>
      <ActionsRow>
        {harvest && (
          <ActionCard>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <ActionTitles>
                <Title>{t('%asset% Earned', { asset: earningToken.symbol })}</Title>
              </ActionTitles>
              <ActionContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <EarnedValue>{account ? displayEarned.toLocaleString() : '?'}</EarnedValue>
                  <StakedValue>{earningToken.symbol}</StakedValue>
                </div>
              </ActionContent>
            </div>
            {account ? (
              <StyledPrimaryButton
                disabled={!earnings.toNumber() || pendingTx}
                ml="4px"
                onClick={async () => {
                  setPendingTx(true)
                  await onReward()
                  setPendingTx(false)
                }}
              >
                {pendingTx ? t('Collecting') : t('Harvest')}
              </StyledPrimaryButton>
            ) : (
              <UnlockButton ml="4px" />
            )}
          </ActionCard>
        )}
        <ActionCard>
          {!account ? (
            <>
              <ActionTitles>
                <Subtle style={{ color: '#ffffff95' }}>{t('Start Staking')}</Subtle>
              </ActionTitles>
              <ActionContent>
                <UnlockButton width="100%" />
              </ActionContent>
            </>
          ) : needsApproval ? (
            <>
              <ActionTitles>
                <Subtle style={{ color: '#ffffff95' }}>{t('Enable Pool')}</Subtle>
              </ActionTitles>
              <ActionContent>
                <StyledPrimaryButton width="100%" disabled={isFinished || requestedApproval} onClick={handleApprove}>
                  {t('Enable')}
                </StyledPrimaryButton>
              </ActionContent>
            </>
          ) : stakedBalance.gt(0) ? (
            <>
              <ActionTitles>
                <Title>{stakingToken.symbol}</Title>
                <Subtle>{t('Staked')}</Subtle>
              </ActionTitles>
              <ActionContent>
                <div>
                  <EarnedValue>{displayStaked.toLocaleString()}</EarnedValue>
                  <StakedValue>{stakingToken.symbol}</StakedValue>
                </div>
                <IconButtonWrapper>
                  <IconButton
                    style={{ borderRadius: '12px', border: '4px solid #fe7b2a' }}
                    variant="secondary"
                    onClick={onPresentWithdraw}
                    disabled={pendingTx}
                  >
                    <MinusIcon color="primary" width="14px" />
                  </IconButton>
                  <IconButton
                    style={{ borderRadius: '12px', border: '4px solid #fe7b2a' }}
                    variant="secondary"
                    onClick={onPresentDeposit}
                    disabled={isFinished && sousId !== 0}
                  >
                    <AddIcon color="primary" width="14px" />
                  </IconButton>
                </IconButtonWrapper>
              </ActionContent>
            </>
          ) : (
            <>
              <ActionTitles>
                <Subtle>{t('Stake %symbol%', { symbol: stakingToken.symbol })}</Subtle>
              </ActionTitles>
              <ActionContent>
                <StyledPrimaryButton width="100%" onClick={onPresentDeposit} disabled={isFinished && sousId !== 0}>
                  {t('Stake')}
                </StyledPrimaryButton>
              </ActionContent>
            </>
          )}
        </ActionCard>
      </ActionsRow>
    </Container>
  )
}

export default PoolActionPanel
